import type { Scene } from '@datum-sdk/engine';
import { isCdnUrl } from '../../utils/cdnUrl';
import { fetchWithTtfbTimeout } from '../../utils/fetchWithTtfbTimeout';
import { loadWithCdnFallback, type AssetType } from '../../utils/loadWithCdnFallback';

/** Resolves once the headers arrive; the body is dropped before it downloads. */
const probe = async (url: string): Promise<string> => {
  const response = await fetchWithTtfbTimeout(url);
  void response.body?.cancel().catch(() => {});
  if (!response.ok) throw new Error(`HTTP ${response.status} ${url}`);
  return url;
};

/**
 * The SDK fetches a scene's assets itself, past `loadWithCdnFallback`: no TTFB
 * bound and no same-origin retry, so an unreachable CDN would hang the scene
 * with no upper bound. Before `loadScene`, every
 * CDN asset of `scene` — splat, preview, sky — is probed with legacy's TTFB
 * timeout, and one whose CDN does not answer is swapped, in place, for its
 * local copy, with legacy's `Asset CDN Fallback` event and log. Never rejects.
 *
 * ponytail: a CDN that answers and then fails mid-download still ends on the
 * load-error screen; its Reload probes again and takes the local copy then.
 */
export const swapUnreachableCdnAssets = async (scene: Scene, sceneId: string): Promise<void> => {
  const probes = new Map<string, Promise<string>>();
  const reachable = (url: string, assetType: AssetType): Promise<string> => {
    if (!isCdnUrl(url)) return Promise.resolve(url);
    let pending = probes.get(url);
    if (!pending) {
      // Neither answered: hand the SDK the CDN URL, so its own error path ends the load.
      pending = loadWithCdnFallback(url, probe, { assetType, sceneId }).catch(() => url);
      probes.set(url, pending);
    }
    return pending;
  };

  const { appearance, environment } = scene.settings ?? {};
  const skybox = environment?.skybox;
  await Promise.all([
    ...(scene.models ?? []).map(async (model) => {
      model.file.fileUrl = await reachable(model.file.fileUrl, 'splat');
    }),
    (async () => {
      if (appearance?.previewModelUrl) {
        appearance.previewModelUrl = await reachable(appearance.previewModelUrl, 'preview-glb');
      }
    })(),
    (async () => {
      if (!skybox || skybox.mode === 'cubemap') return;
      const { url2k } = skybox;
      const [url, small] = await Promise.all([
        reachable(skybox.url, 'skybox'),
        url2k ? reachable(url2k, 'skybox') : undefined,
      ]);
      skybox.url = url;
      if (small) skybox.url2k = small;
    })(),
  ]);
};
